import type { Config } from "tailwindcss";
import defaultTheme from "tailwindcss/defaultTheme.js";

/**
 * Generates an object with keys as numbers and values as strings representing the size in rem.
 * Unlike the default Tailwind CSS sizes, this function leaves always a 2-px gap between each size.
 *
 * @param max Maximum value to generate the size for
 * @returns  Object with keys as numbers and values as strings representing the size in rem
 */
function generate(max: number): Record<number, string> {
	const object: Record<number, string> = {
		3.25: "0.8125rem", // 13px
		4.75: "1.1875rem", // 19px
	};

	// Generate for each 2 pixels from 0 to max
	for (let i = 0; i <= max; i += 0.5) {
		object[i] = `${i / 4}rem`;
	}

	return object;
}

const SIZES = {
	50: generate(50),
	100: generate(100),
	200: generate(200),
};

export default {
	darkMode: "class",
	theme: {
		fontSize: {
			"5xs": "0.5rem",
			"4xs": "0.625rem",
			"3xs": "0.7rem",
			"2xs": "0.75rem",
			xs: "0.8125rem", // menu items
			sm: "0.875rem", // small
			"2sm": "0.9375rem", // small
			base: "1rem", // base
			md: ["1.0625rem", "1.625rem"], // also base :)
			title1: ["4rem", "4.5rem"],
			title2: ["2rem", "2.625rem"],
			title3: ["1.5rem", "1.875rem"],
			"2md": "1.375rem",
			lg: "2rem",
			"2lg": "2.375rem",
			xl: "2.75rem", // H1
			"2xl": "3.2rem",
			"3xl": "3.5rem",
			"4xl": "4.625rem",
			"5xl": "8rem",
			body17: ["1.0625rem", "1.625rem"],
			caption: ["0.8125rem", "1.25rem"],
		},
		screens: {
			xs: "480px",
			sm: "640px",
			md: "768px",
			lg: "1024px",
			xl: "1280px",
			"2xl": "1439px",
		},
		extend: {
			fontFamily: {
				sans: ["FigTree", ...defaultTheme.fontFamily.sans],
				FigTree: ["FigTree"],
			},
			// Prefix colors with shortcut of project name (example: fastest -> fstgreen)
			colors: {
				// #endregion
				"nov-dark": {
					DEFAULT: "#1C1B1F",
					800: "#494D5B",
					900: "#1B1D22",
				},
				"nov-light": {
					"000": "#FFFFFF",
					100: "#F8F8F9",
					200: "#E8E8EA",
					300: "#C8C8CA",
				},
				"nov-red": {
					100: "#FF858D",
					200: "#e0000f",
					300: "#a3000b",
				},
				"nov-green": {
					100: "#80B752",
				},
			},
			width: SIZES[200],
			height: SIZES[200],
			maxWidth: SIZES[200],
			maxHeight: SIZES[200],
			minWidth: SIZES[200],
			minHeight: SIZES[200],
			margin: SIZES[100],
			lineHeight: {
				...SIZES[50],
				lg: "1.6875rem", // 27px
			},
			padding: SIZES[100],
			spacing: SIZES[100],
			gap: SIZES[100],
			fontSize: SIZES[50],
			flexBasis: SIZES[200],
		},
	},
} satisfies Partial<Config>;
